@layer refine.base {
  :host {
    /* ── Datagrid-specific token overrides on top of tableBaseMixin ─────── */
    --_row-hover-bg: var(--datagrid-row-hover-bg, color-mix(in oklch, var(--color-contrast-500) 5%, transparent));
    --_row-selected-bg: var(--datagrid-row-selected-bg, color-mix(in oklch, var(--_accent) 10%, transparent));
    --_row-selected-border: var(--datagrid-row-selected-border, var(--_accent));
  }

  /* ── Scroll wrapper ───────────────────────────────────────────────────────── */

  .dg-scroll {
    max-height: var(--datagrid-max-height, none);
    overflow: auto;
  }

  /* ── Table ────────────────────────────────────────────────────────────────── */

  .dg-table {
    width: 100%;
    font-size: var(--_font-size);
    table-layout: auto;
    border-collapse: collapse;
  }

  /* ── Head ─────────────────────────────────────────────────────────────────── */

  .dg-head {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--_header-bg);
    box-shadow: inset 0 calc(var(--border) * -1) 0 var(--_border-color);
  }

  .dg-th {
    padding: var(--_cell-padding-y) var(--_cell-padding-x);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-500);
    text-align: start;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    user-select: none;
    border-right: var(--border) solid var(--_border-color);

    &[data-align='center'] {
      text-align: center;
    }

    &[data-align='right'] {
      text-align: right;
    }

    &:has(.dg-sort-btn) {
      padding: 0;
    }
  }

  .dg-sort-btn {
    --button-padding: var(--_cell-padding-y) var(--_cell-padding-x);
    --button-font-size: var(--text-xs);
    --button-font-weight: var(--font-semibold);
    --button-color: var(--color-contrast-500);
    --button-justify: flex-start;

    height: 100%;
  }

  .dg-th[data-align='center'] .dg-sort-btn {
    --button-justify: center;
  }

  .dg-th[data-align='right'] .dg-sort-btn {
    --button-justify: flex-end;
  }

  :host(:dir(rtl)) .dg-th,
  :dir(rtl) .dg-th {
    border-right: none;
    border-left: var(--border) solid var(--_border-color);
  }

  .dg-th:last-child,
  .dg-th.dg-th-check,
  .dg-th.dg-th-expand,
  .dg-th.dg-th-last {
    border-right: none;
  }

  :host(:dir(rtl)) .dg-th:last-child,
  :host(:dir(rtl)) .dg-th.dg-th-check,
  :host(:dir(rtl)) .dg-th.dg-th-expand,
  :host(:dir(rtl)) .dg-th.dg-th-last,
  :dir(rtl) .dg-th:last-child,
  :dir(rtl) .dg-th.dg-th-check,
  :dir(rtl) .dg-th.dg-th-expand,
  :dir(rtl) .dg-th.dg-th-last {
    border-left: none;
  }

  .dg-th-inner {
    position: relative;
    display: flex;
    gap: var(--size-1);
    align-items: center;
  }

  .dg-th[aria-sort]:has(.dg-sort-btn:hover):not(:has(.dg-sort-btn[aria-disabled='true'])) {
    background: color-mix(in oklch, var(--color-contrast-900) 5%, transparent);
  }

  .dg-th[aria-sort='ascending'] .dg-sort-icon,
  .dg-th[aria-sort='descending'] .dg-sort-icon {
    color: var(--_accent);
  }

  /* Select-all checkbox cell */
  .dg-th-check {
    width: var(--size-5);
    min-width: var(--size-5);
    padding: var(--_cell-padding-y) var(--_cell-padding-x);
  }

  .dg-th-check,
  .dg-td-check {
    vertical-align: middle;
    text-align: center;
  }

  .dg-td.dg-td-check {
    padding: var(--size-1) 0 0;
  }

  /* ore-checkbox used for select-all and row checkboxes — label-less, compact */
  .dg-check {
    display: inline-flex;
    gap: 0;
    align-items: center;
    justify-content: center;
    min-height: 0;
    cursor: pointer;
  }

  /* ── Body rows ────────────────────────────────────────────────────────────── */

  .dg-body .dg-tr {
    border-block-end: var(--border) solid var(--_border-color);
    transition: background var(--transition-fast);
  }

  .dg-body .dg-tr:hover:not([data-disabled], [aria-selected='true']) {
    background: var(--_row-hover-bg);
  }

  /* ── Selection ─────────────────────────────────────────────────────────────── */

  .dg-body .dg-tr[aria-selected='true'] {
    background: var(--_row-selected-bg);
  }

  .dg-body .dg-tr[data-selectable] {
    cursor: pointer;
    outline: none;
  }

  .dg-body .dg-tr[data-selectable]:focus-visible {
    box-shadow: inset 0 0 0 2px var(--_row-selected-border);
  }

  .dg-body .dg-tr[data-disabled] {
    pointer-events: none;
    opacity: 0.45;
  }

  /* Striped rows */
  :host([striped]) .dg-body .dg-tr:nth-child(even) {
    background: var(--_stripe-bg);
  }

  :host([striped]) .dg-body .dg-tr:nth-child(even)[aria-selected='true'] {
    background: var(--_row-selected-bg);
  }

  /* ── Body cells ────────────────────────────────────────────────────────────── */

  .dg-td {
    max-width: var(--datagrid-cell-max-width, 32ch);
    padding: var(--_cell-padding-y) var(--_cell-padding-x);
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    color: var(--color-contrast-800);
    white-space: nowrap;
    border-right: var(--border) solid var(--_border-color);

    &[data-align='center'] {
      text-align: center;
    }

    &[data-align='right'] {
      text-align: right;
    }
  }

  :host(:dir(rtl)) .dg-td,
  :dir(rtl) .dg-td {
    border-right: none;
    border-left: var(--border) solid var(--_border-color);
  }

  .dg-td:last-child,
  .dg-td.dg-td-check,
  .dg-td.dg-td-expand,
  .dg-td.dg-td-last {
    border-right: none;
  }

  :host(:dir(rtl)) .dg-td:last-child,
  :host(:dir(rtl)) .dg-td.dg-td-check,
  :host(:dir(rtl)) .dg-td.dg-td-expand,
  :host(:dir(rtl)) .dg-td.dg-td-last,
  :dir(rtl) .dg-td:last-child,
  :dir(rtl) .dg-td.dg-td-check,
  :dir(rtl) .dg-td.dg-td-expand,
  :dir(rtl) .dg-td.dg-td-last {
    border-left: none;
  }

  /* ── Empty state ──────────────────────────────────────────────────────────── */

  .dg-empty {
    padding: var(--size-12) var(--_cell-padding-x);
    font-size: var(--text-sm);
    color: var(--color-contrast-400);
    text-align: center;
  }

  .dg-empty-content {
    display: flex;
    flex-direction: column;
    gap: var(--size-4);
    align-items: center;
    justify-content: center;
  }

  .dg-empty-actions {
    margin-top: var(--size-2);
  }

  /* ── Controls bar (tabs + action row) ───────────────────────────────────── */

  .dg-controls {
    display: flex;
    gap: var(--size-3);
    align-items: center;
    justify-content: space-between;
    height: var(--size-8);
    padding: var(--size-1-5) var(--_cell-padding-x);
    background: var(--_header-bg);
    border-block-end: var(--border) solid var(--_border-color);
  }

  /* ── Tab system (left) ───────────────────────────────────────────────────── */

  .dg-tabs {
    display: flex;
    flex: 1;
    gap: var(--size-1);
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }

  .dg-tab {
    --button-padding: var(--size-0-5) var(--size-2-5);
    --button-font-size: var(--text-xs);
    --button-color: var(--color-contrast-500);

    flex: 1;
    min-width: 0;
  }

  .dg-tab--active {
    --button-color: var(--color-contrast-900);
    --button-bg: color-mix(in oklch, var(--color-contrast-500) 10%, transparent);
    --button-border-color: var(--_border-color);
    --button-border: var(--border);

    font-weight: var(--font-semibold);
  }

  /* ── Action bar (right) ──────────────────────────────────────────────────── */

  .dg-actions {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-0-5);
    align-items: center;
  }

  /* ── Shared icon button — used by action bar, search-expand clear, popover header icons ── */

  .dg-action-divider {
    display: inline-block;
    flex-shrink: 0;
    width: var(--border);
    height: var(--size-4);
    margin-inline: var(--size-1);
    background: var(--_border-color);

    &[hidden] {
      display: none;
    }
  }

  .dg-filter {
    display: block;
    width: 100%;
  }

  /* ── Search toggle (rightmost action) ───────────────────────────────────── */

  .dg-search {
    display: flex;
    flex-direction: row-reverse;
    gap: var(--size-1);
    align-items: center;
  }

  .dg-search-input {
    --input-height: var(--size-7);
    display: block;
    flex-shrink: 0;
    width: 0;
    overflow: hidden;
    opacity: 0;
    transition:
      width var(--transition-fast),
      opacity var(--transition-fast);
  }

  .dg-search--open .dg-search-input {
    width: 12rem;
    overflow: visible;
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .dg-search-input {
      transition: none;
    }
  }

  /* ── ore-popover: action popover wrapper ─────────────────────────────────── */

  .dg-action-popover {
    display: inline-flex;
  }

  /* ── Popover content: shared header row ─────────────────────────────────── */

  .dg-pop-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--size-3) var(--size-3) var(--size-2);
  }

  .dg-pop-title {
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-600);
    letter-spacing: 0.02em;
  }

  /* ── Sort / Group by popover content ────────────────────────────────────── */

  .dg-pop-sort {
    display: flex;
    flex-direction: column;
    padding-block-end: var(--size-2);
  }

  .dg-pop-sort-row {
    display: flex;
    gap: var(--size-2);
    align-items: center;
    padding: var(--size-1) var(--size-3);
  }

  .dg-pop-select {
    --select-min-width: var(--size-20);

    flex: 2;
  }

  .dg-pop-dir-select {
    --select-min-width: var(--size-10);

    flex: 1;
  }

  /* ── Filter popover content ──────────────────────────────────────────────── */

  .dg-pop-filter {
    display: flex;
    flex-direction: column;
  }

  /* Field picker: select columns to add filter rules. */
  .dg-pop-filter-fields {
    padding: 0 var(--size-3) var(--size-3);
  }

  /* ── Filter rules section ────────────────────────────────────────────────── */

  .dg-pop-filter-rules {
    padding-block-start: var(--size-1);
    border-block-start: var(--border) solid var(--_border-color);

    &[hidden] {
      display: none;
    }
  }

  .dg-pop-filter-rule {
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    padding: var(--size-2) var(--size-3);
  }

  .dg-pop-filter-rule-header {
    display: flex;
    gap: var(--size-1-5);
    align-items: center;
  }

  .dg-pop-filter-field {
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-800);
    white-space: nowrap;
  }

  .dg-pop-filter-op-select {
    --select-min-width: 0;
    --select-height: var(--size-6);
    --select-font-size: var(--text-2xs);
    --select-padding-x: var(--size-1-5);

    flex: 1;
    min-width: 6rem;
  }

  .dg-pop-col-item {
    --button-radius: var(--rounded-md);

    justify-content: flex-start;
  }

  .dg-pop-col-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--size-1);
    max-width: 25rem;
    padding: var(--size-3);
  }

  /* ── Footer / pagination bar ─────────────────────────────────────────────── */

  .dg-footer {
    display: flex;
    gap: var(--size-2);
    align-items: center;
    padding: var(--size-2) var(--_cell-padding-x);
    font-size: var(--text-xs);
    color: var(--color-contrast-500);
    background: var(--_header-bg);
  }

  .dg-footer-info {
    white-space: nowrap;
  }

  .dg-footer-end {
    display: flex;
    gap: var(--size-2);
    align-items: center;
    width: fit-content;
    margin-inline-start: auto;
  }

  .dg-page-size-wrap {
    flex-shrink: 0;
    width: 5rem;
  }

  .dg-page-size-select {
    --select-min-width: 0;
    --select-height: var(--size-7);
    --select-font-size: var(--text-xs);
    --select-padding-x: var(--size-2);
  }

  .dg-pagination {
    display: flex;
    gap: var(--size-1);
    align-items: center;
  }

  :host(:dir(rtl)) .dg-page-btn,
  :dir(rtl) .dg-page-btn {
    transform: scaleX(-1);
  }

  .dg-page-btn {
    --button-color: var(--color-contrast-600);
    --button-radius: var(--rounded-md);

    width: var(--size-7);
    height: var(--size-7);
  }

  .dg-page-btn:hover {
    --button-color: var(--color-contrast-900);
    --button-bg: var(--color-contrast-300);
  }

  .dg-page-btn:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }

  .dg-page-label {
    padding-inline: var(--size-1);
    white-space: nowrap;
  }

  /* ── Column resize handle (F4) ───────────────────────────────────────────── */

  .dg-col-resize {
    position: absolute;
    inset-block: calc(var(--_cell-padding-y) * -1);
    inset-inline-end: calc((var(--_cell-padding-x) + var(--border) + 2px) * -1);
    width: var(--size-1);
    cursor: col-resize;
    user-select: none;
    background: transparent;
    border-radius: var(--rounded);
    transition: background var(--transition-fast);
  }

  .dg-col-resize:hover,
  .dg-col-resize:active {
    background: var(--_accent, var(--color-contrast-300));
  }

  /* ── Cell keyboard focus (ARIA grid roving tabindex) ─────────────────────── */

  .dg-td:focus-visible,
  .dg-th:focus-visible {
    outline: 2px solid var(--_row-selected-border);
    outline-offset: -2px;
  }

  /* ── Row expansion ────────────────────────────────────────────────────────── */

  .dg-tr-expanded .dg-td-expanded {
    padding: var(--_cell-padding-y) var(--_cell-padding-x);
    border: none;

    /* Paint background + bottom separator via box-shadow so it is
       direction-agnostic and covers the host outer border on both sides. */
    box-shadow:
      inset 0 0 0 9999px color-mix(in oklch, var(--color-contrast-600) 4%, var(--_bg, transparent)),
      inset 0 calc(var(--border) * -1) 0 var(--_border-color);
  }

  .dg-tr-expanded:last-child .dg-td-expanded {
    box-shadow: inset 0 0 0 9999px color-mix(in oklch, var(--color-contrast-900) 4%, var(--_bg, transparent));
  }

  .dg-tr:has(+ .dg-tr-expanded) {
    border-block-end: none;
  }

  .dg-th-expand,
  .dg-td-expand {
    width: var(--size-8);
    min-width: var(--size-8);
    padding-block: var(--size-1) 0;
    padding-inline: 0 var(--size-2);
    text-align: center;
    white-space: nowrap;
    border-right: none;
    border-left: none;
  }

  .dg-expand-btn {
    --button-color: var(--color-contrast-500);
    --button-radius: var(--rounded-sm);

    width: var(--size-5);
    height: var(--size-5);
  }

  .dg-expand-btn:hover {
    --button-color: var(--color-contrast-900);
  }

  .dg-expand-btn:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  /* ── Loading overlay ──────────────────────────────────────────────────────── */

  .dg-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: none;
    align-items: center;
    justify-content: center;
    background: color-mix(in oklch, var(--_bg, white) 40%, transparent);
    backdrop-filter: blur(1px);
  }

  :host([loading]) .dg-loading-overlay {
    display: flex;
  }

  :host([loading]) .dg-scroll table {
    pointer-events: none;
    opacity: 0.5;
  }

  .dg-spin {
    animation: dg-spin 1s linear infinite;
  }

  @keyframes dg-spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
}
